import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { combineStrings } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { compact, keys } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Product from 'models/productManagement/product/Model.Product';
import VehicleModel from 'models/productManagement/vehicleModel/Model.VehicleModel';
import Contract from 'models/sales/contract/Model.Contract';
import contractStatusFlowApi from 'models/sales/contract/statusFlow/Api.Contract.StatusFlow';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useMemo } from 'react';
import { contractIndexRoute } from './Contract.Index';

export default function useContract() {
  const { lang } = getLocales();
  const { permissions, user, isDealer, isDistributor } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.Contract.Params, Module.Claims.Contract.Query>({
    route: contractIndexRoute,
  });

  const hasAccess =
    permissions.EDIT_CLAIM ||
    permissions.CLAIM_ADMIN ||
    permissions.CREATE_CLAIM ||
    permissions.CREATE_SIMPLE_CLAIM ||
    permissions.CREATE_CLAIM_FOR_EXPIRED_CONTRACT;
  const listApi = useApi(
    {
      action: Contract.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user?.distributorId ?? undefined,
        includeClaims: true,
      },
      body: pageRouter.query.list,
      wait: !hasAccess,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const dealersApi = useApi({
    action: Dealer.list,
    wait: true,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      isInactive: false,
    },
  });
  const distributorsApi = useApi({
    action: Distributor.list,
    wait: !isDealer && !isDistributor,
  });

  const userListApi = useApi({
    action: User.list,
    default: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !hasAccess || !permissions.CREATE_CONTRACT,
  });

  const productsApi = useApi({
    action: Product.list,
    wait: true,
    default: {
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
  });

  const modelsApi = useApi({
    action: VehicleModel.list,
    wait: true,
  });

  const contractStatusInfoApi = useApi({ action: contractStatusFlowApi });
  const contractStatusInfo = contractStatusInfoApi.payload;

  const statuses = useMemo(() => {
    return keys(contractStatusInfo).map((status) => {
      const [primary, secondary, reason] = (status ? lang[status] : status).split('_');
      const mainStatus = combineStrings(' - ', primary, secondary);
      return {
        id: status,
        subtitle: mainStatus,
        title: reason ?? mainStatus,
      };
    });
  }, [contractStatusInfo]);

  return {
    hasAccess,
    pageRouter,
    listApi,
    userListApi,
    selected,
    dealersApi,
    distributorsApi,
    modelsApi,
    productsApi,
    permissions,
    statuses,
    isDealer,
    isDistributor,
  };
}

export function useContractProvider() {
  return useDataProvider<ReturnType<typeof useContract>>();
}
